import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, SlidersHorizontal } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useProspects } from "../../context/ProspectsContext";
import { PROSPECT_STATUS } from "../../constants/config";
import { ProspectCard } from "../../components/sales/ProspectCard";

const FILTER_OPTIONS = [
  { value: "all", label: "Semua" },
  ...Object.entries(PROSPECT_STATUS).map(([key, val]) => ({
    value: key,
    label: val.label,
    color: val.color,
    bg: val.bg,
  })),
];

export default function Prospects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { prospects } = useProspects();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  // Backend sudah filter berdasarkan role — tidak perlu filter lokal
  const myProspects = prospects;

  const filtered = useMemo(() => {
    let list = myProspects;
    if (filter !== "all") list = list.filter((p) => p.status === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.address || "").toLowerCase().includes(q) ||
          (p.phone || "").includes(q)
      );
    }
    return list;
  }, [myProspects, filter, query]);

  const counts = useMemo(() => {
    const c = { all: myProspects.length };
    Object.keys(PROSPECT_STATUS).forEach((k) => {
      c[k] = myProspects.filter((p) => p.status === k).length;
    });
    return c;
  }, [myProspects]);

  return (
    <div data-testid="prospects-page" className="pb-4">
      {/* Header */}
      <div className="bg-white border-b border-honda-border px-5 pt-12 pb-4 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button
            data-testid="prospects-back-button"
            onClick={() => navigate("/sales/dashboard")}
            className="w-9 h-9 rounded-full hover:bg-honda-bg flex items-center justify-center -ml-1.5"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-xl text-honda-ink leading-tight">
              Prospek Saya
            </h1>
            <p className="text-xs text-honda-muted">
              {myProspects.length} total · {counts.hot || 0} hot · {counts.closing || 0} closing
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-honda-subtle"
          />
          <input
            data-testid="prospects-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari nama, alamat, telepon..."
            className="honda-input pl-10"
          />
        </div>

        {/* Filter chips */}
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 pb-1">
          {FILTER_OPTIONS.map((opt) => {
            const active = filter === opt.value;
            return (
              <button
                key={opt.value}
                data-testid={`filter-${opt.value}`}
                onClick={() => setFilter(opt.value)}
                className={`whitespace-nowrap text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? "bg-honda-red text-white border-honda-red"
                    : "bg-white text-honda-muted border-honda-border hover:border-honda-red"
                }`}
              >
                {opt.label} · {counts[opt.value] || 0}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="px-5 pt-4 space-y-2.5">
        {filtered.length === 0 ? (
          <div className="honda-card p-8 text-center mt-4">
            <SlidersHorizontal className="mx-auto text-honda-subtle" size={28} />
            <p className="mt-2 text-sm text-honda-muted">
              Tidak ada prospek yang sesuai filter.
            </p>
          </div>
        ) : (
          filtered.map((p) => <ProspectCard key={p.id} prospect={p} />)
        )}
      </div>
    </div>
  );
}
